/**
 * TableTabs — top-tab category strip on /table.
 *
 * Founder spec (8 Feb 2026):
 *   Tabs: [All] [Michelin] [Fine Dine] [Bars] [Nightclubs] [Destinations]
 *   - Exclusive: a star-holder appears under [Michelin] only.
 *   - No count badges — clean pills.
 *   - Deep-link URL: /table?tab=michelin
 *   - Epicure rows surface under [All] only · dropped from the strip.
 *
 * Founder spec extension (11 Feb 2026 PM):
 *   Concierge / admin sessions ALSO see a 7th [Curate] tab — a
 *   convenience-entry into the existing /table/curate studio. Members
 *   never see it. Two-lock defence: this prop hides it at the tab
 *   strip, and `TableCurate.jsx` still bounces non-console sessions
 *   to /mira/console/login if they URL-hack their way in.
 *
 * Renders as a quiet horizontal pill row. The active tab gets the
 * brass accent treatment; inactive tabs sit in ink-2. Keyboard-
 * navigable (arrow keys), tap-target ≥ 44px.
 */
import React from "react";

const MEMBER_TABS = [
    { key: "all",          label: "All" },
    { key: "michelin",     label: "Michelin" },
    { key: "fine_dine",    label: "Fine Dine" },
    { key: "bars",         label: "Bars" },
    { key: "nightclubs",   label: "Nightclubs" },
    { key: "destinations", label: "Destinations" },
];

// The Curate tab is a NAVIGATION button (it routes to /table/curate),
// not a category filter. We keep it in the same strip for muscle-
// memory, but it carries a distinct `kind: 'nav'` marker so the
// parent knows to navigate instead of setTab.
const CURATE_TAB = {
    key: "curate",
    label: "Curate",
    kind: "nav",
    target: "/table/curate",
};

export function TableTabs({ value, onChange, showCurate = false }) {
    const tabs = showCurate ? [...MEMBER_TABS, CURATE_TAB] : MEMBER_TABS;
    return (
        <div
            data-testid="table-tabs"
            role="tablist"
            aria-label="Category"
            style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                padding: "8px 0",
                borderBottom: "1px solid var(--t-line)",
                marginBottom: 24,
            }}
        >
            {tabs.map((tab) => {
                const active = value === tab.key;
                const isNav = tab.kind === "nav";
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        data-testid={`table-tab-${tab.key}`}
                        aria-selected={active}
                        onClick={() => onChange(tab.key, tab)}
                        style={{
                            background: active
                                ? "var(--t-ink)"
                                : "transparent",
                            color: active
                                ? "var(--t-paper2)"
                                : (isNav ? "var(--t-brass)" : "var(--t-ink2)"),
                            border: active
                                ? "1px solid var(--t-ink)"
                                : (isNav
                                    ? "1px dashed var(--t-brass)"
                                    : "1px solid transparent"),
                            borderRadius: 999,
                            padding: "10px 18px",
                            minHeight: 44,
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            fontSize: 13,
                            fontWeight: active ? 700 : (isNav ? 600 : 500),
                            letterSpacing: isNav ? "0.04em" : "0.01em",
                            textTransform: isNav ? "uppercase" : undefined,
                            cursor: "pointer",
                            transition: "all 0.18s",
                        }}
                        onMouseEnter={(e) => {
                            if (active) return;
                            e.currentTarget.style.color = isNav
                                ? "var(--t-ink)"
                                : "var(--t-ink)";
                            e.currentTarget.style.borderColor = isNav
                                ? "var(--t-brass)"
                                : "var(--t-line2)";
                        }}
                        onMouseLeave={(e) => {
                            if (active) return;
                            e.currentTarget.style.color = isNav
                                ? "var(--t-brass)"
                                : "var(--t-ink2)";
                            e.currentTarget.style.borderColor = isNav
                                ? "var(--t-brass)"
                                : "transparent";
                        }}
                    >
                        {tab.label}
                        {isNav ? (
                            <span
                                aria-hidden="true"
                                style={{
                                    marginLeft: 6,
                                    fontSize: 10,
                                    opacity: 0.7,
                                }}
                            >
                                ↗
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

export const TAB_KEYS = MEMBER_TABS.map((t) => t.key);
